import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { RegularModal } from '@/components_deprecated/Modal'
import { DetectBlur } from '@/containers/util-containers'
import type { OverrideOrAddonForm } from './types'
import { Form } from '@/components_deprecated/Form'
import {
  Checkbox,
  DatePicker,
  TextArea,
  TimePicker,
} from '@/components_deprecated'
import { SalaryCodeSelect } from '@/components_deprecated/Select/SalaryCodeSelect'
import styled from 'styled-components'
import { set } from 'date-fns'

interface FieldProps {
  alignLeft?: boolean
  alignRight?: boolean
  area?: string
}
export const Field = styled.div<FieldProps>`
  grid-area: ${props => props.area};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  ${props => props.alignLeft && 'align-items: flex-start;'}
  ${props => props.alignRight && 'align-items: flex-end;'}

  & > label {
    font-size: 0.9rem;
    font-weight: 600;
    margin: 0 0 6px 6px;
    color: ${props => props.theme.colors.gray5};
  }
`

const StyledForm = styled(Form)`
  gap: 0.5rem;
`

const DateTimeField = styled(Field)`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 0.5rem;

  label {
    grid-row: 1;
    grid-column: 1 / 3;
    margin: 0 0 0 0;
  }
  ${props => props.theme.media.mobile} {
    .--time-picker-inner-wrapper {
      width: 80px;
      padding: 0;
    }
  }
`

interface EditAddonPromptProps {
  initialData?: OverrideOrAddonForm
  isOverride?: boolean
  isConfiguration?: boolean
  userId?: string
  onSubmit: (data: OverrideOrAddonForm | null) => void
}

export const EditAddonPrompt: React.FC<EditAddonPromptProps> = ({
  initialData,
  isOverride,

  isConfiguration = false,

  userId,

  onSubmit,
}) => {
  const { formValues, formChangeHandler, formValid, updateForm } =
    useForm<OverrideOrAddonForm>({
      values: initialData ?? {
        datetimeStart: new Date(),
        datetimeEnd: new Date(),
        salaryCode: '',
        overrideOtherAddons: false,
        comment: '',
      },
      config: {
        initAsInvalid: true,
      },
      validators: {
        datetimeStart: (value, other) => {
          if (!value || !other.datetimeEnd) {
            return null
          }
          return value < other.datetimeEnd
            ? null
            : translations.error.startBeforeEnd
        },
        datetimeEnd: (value, other) => {
          if (!value || !other.datetimeStart) {
            return null
          }
          return value > other.datetimeStart
            ? null
            : translations.error.startBeforeEnd
        },
        salaryCode: value => {
          return value ? null : translations.error.required
        },
      },
    })

  const translations = useTranslate({
    label: {
      datetimeStart: 'common.start',
      datetimeEnd: 'common.end',
      salaryCode: 'common.salary-code',
      overrideOtherAddons: 'salary.override-other-addons',
      comment: 'common.comment',
    },
    create: 'common.create-alt',
    update: 'common.update',

    error: {
      startBeforeEnd: 'timesheets.error.start-before-end',
      required: 'common.required',
    },
  })

  function updateTime(key: 'datetimeStart' | 'datetimeEnd') {
    return (value: Date) => {
      const existingValue = formValues[key]
      if (!existingValue) {
        return
      }
      const newValue = set(existingValue, {
        hours: value.getHours(),
        minutes: value.getMinutes(),
      })

      updateForm({
        [key]: newValue,
      })
    }
  }

  function updateDate(key: 'datetimeStart' | 'datetimeEnd') {
    return (value: Date) => {
      const existingValue = formValues[key]
      if (!existingValue) {
        return
      }
      const newValue = set(existingValue, {
        year: value.getFullYear(),
        month: value.getMonth(),
        date: value.getDate(),
      })

      updateForm({
        [key]: newValue,
      })
    }
  }

  return (
    <DetectBlur active onClickOutside={() => onSubmit(null)}>
      <RegularModal
        title={translations.create}
        submitText={translations.update}
        submitDisabled={!formValid}
        onCancel={() => onSubmit(null)}
        onSubmit={() => onSubmit(formValues)}
      >
        <StyledForm onSubmit={() => onSubmit(formValues)}>
          {formValues.datetimeStart && (
            <DateTimeField>
              <label>{translations.label.datetimeStart}</label>
              <TimePicker
                value={formValues.datetimeStart}
                width="100px"
                onChange={updateTime('datetimeStart')}
                grayed
              />

              <DatePicker
                value={formValues.datetimeStart}
                onChange={updateDate('datetimeStart')}
                grayed
              />
            </DateTimeField>
          )}
          {formValues.datetimeEnd && (
            <DateTimeField>
              <label>{translations.label.datetimeEnd}</label>
              <TimePicker
                value={formValues.datetimeEnd}
                width="100px"
                onChange={updateTime('datetimeEnd')}
                grayed
              />
              <DatePicker
                value={formValues.datetimeEnd}
                onChange={updateDate('datetimeEnd')}
                grayed
              />
            </DateTimeField>
          )}
          <Field>
            <label>{translations.label.salaryCode}</label>
            <SalaryCodeSelect
              value={formValues.salaryCode}
              onChange={value =>
                value ? formChangeHandler('salaryCode')(value) : null
              }
              isAddon={!isConfiguration && !!userId}
              grayed
              userId={userId}
            />
          </Field>
          {!isOverride &&
            typeof formValues.overrideOtherAddons !== 'undefined' && (
              <Field>
                <label>{translations.label.overrideOtherAddons}</label>
                <Checkbox
                  checked={formValues.overrideOtherAddons}
                  onChange={formChangeHandler('overrideOtherAddons')}
                  grayed
                />
              </Field>
            )}
          <Field>
            <label>{translations.label.comment}</label>
            <TextArea
              value={formValues.comment}
              onChange={formChangeHandler('comment')}
              rows={3}
            />
          </Field>
        </StyledForm>
      </RegularModal>
    </DetectBlur>
  )
}
